import React, { forwardRef } from 'react';
import { theme } from '../../theme';

import { tokenVars } from '../../theme/tokens';
import { withAlpha } from '../../theme/colorUtils';
export interface FormTextareaProps extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'> {
  label?: string;
  error?: string;
  hint?: string;
  size?: 'small' | 'medium' | 'large';
  variant?: 'default' | 'comment' | 'description';
  required?: boolean;
  fullWidth?: boolean;
  minRows?: number;
  maxRows?: number;
}

export const FormTextarea = forwardRef<HTMLTextAreaElement, FormTextareaProps>(
  (
    {
      label,
      error,
      hint,
      size = 'medium',
      variant = 'default',
      required = false,
      fullWidth = true,
      minRows = 3,
      maxRows = 10,
      className = '',
      style = {},
      ...props
    },
    ref
  ) => {
    const sizeConfig = theme.sizes.getSizeConfig(size);

    const variants = {
      default: {
        backgroundColor: tokenVars.bgSubtle,
        border: `1px solid ${tokenVars.border}`,
      },
      comment: {
        backgroundColor: tokenVars.bgSubtle,
        border: `1px solid ${tokenVars.border}`,
      },
      description: {
        backgroundColor: tokenVars.surface,
        border: `1px solid ${tokenVars.border}`,
      },
    };

    const textareaStyles = {
      width: fullWidth ? '100%' : 'auto',
      backgroundColor: variants[variant].backgroundColor,
      border: variants[variant].border,
      borderRadius: theme.borders.input.radius,
      color: tokenVars.text.primary,
      outline: 'none',
      transition: `border-color ${theme.animations.duration.fast}ms ${theme.animations.easing.easeOut}`,
      fontFamily: theme.typography.fontFamily.primary,
      fontSize: sizeConfig.fontSize,
      lineHeight: theme.typography.lineHeight.normal,
      padding: sizeConfig.padding,
      resize: 'vertical' as const,
      minHeight: `${minRows * 1.4 * sizeConfig.fontSize + 2 * parseInt(sizeConfig.padding.split(' ')[0])}px`,
      maxHeight: `${maxRows * 1.4 * sizeConfig.fontSize + 2 * parseInt(sizeConfig.padding.split(' ')[0])}px`,
      ...style,
    };

    const focusStyles = {
      borderColor: tokenVars.focus,
      boxShadow: `0 0 0 2px ${withAlpha(tokenVars.focus, 0.25)}`,
    };

    const errorStyles = error
      ? {
          borderColor: tokenVars.danger,
          boxShadow: `0 0 0 2px ${withAlpha(tokenVars.danger, 0.25)}`,
        }
      : {};

    const labelStyles = {
      display: 'block',
      marginBottom: theme.spacing[1],
      color: tokenVars.text.secondary,
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.medium,
      fontFamily: theme.typography.fontFamily.primary,
      textAlign: 'left' as const,
    };

    const hintStyles = {
      marginTop: theme.spacing[1],
      color: tokenVars.text.secondary,
      fontSize: theme.typography.fontSize.xs,
      fontFamily: theme.typography.fontFamily.primary,
    };

    const errorTextStyles = {
      marginTop: theme.spacing[1],
      color: tokenVars.dangerText,
      fontSize: theme.typography.fontSize.xs,
      fontFamily: theme.typography.fontFamily.primary,
    };

    return (
      <div style={{ width: fullWidth ? '100%' : 'auto' }}>
        {label && (
          <label style={labelStyles}>
            {label}
            {required && <span style={{ color: tokenVars.dangerText }}> *</span>}
          </label>
        )}
        <textarea
          ref={ref}
          style={{
            ...textareaStyles,
            ...errorStyles,
          }}
          className={className}
          rows={minRows}
          onFocus={(e) => {
            Object.assign(e.target.style, focusStyles);
            props.onFocus?.(e);
          }}
          onBlur={(e) => {
            Object.assign(e.target.style, {
              borderColor: error ? tokenVars.danger : tokenVars.border,
              boxShadow: error ? `0 0 0 2px ${withAlpha(tokenVars.danger, 0.25)}` : 'none',
            });
            props.onBlur?.(e);
          }}
          {...props}
        />
        {hint && !error && <div style={hintStyles}>{hint}</div>}
        {error && <div style={errorTextStyles}>{error}</div>}
      </div>
    );
  }
);
